import { Link, useParams } from "react-router-dom";
import { CONDITIONS, CONDITIONS_BY_SLUG } from "@/data/conditions";
import BookingButton from "@/components/BookingButton";
import FAQ from "@/components/FAQ";
import { useEffect } from "react";
import { ArrowRight, Check } from "lucide-react";

function setMeta(title, description) {
  if (typeof document === "undefined") return;
  document.title = title;
  let m = document.querySelector('meta[name="description"]');
  if (!m) {
    m = document.createElement("meta");
    m.setAttribute("name", "description");
    document.head.appendChild(m);
  }
  m.setAttribute("content", description);
}

export function ConditionsIndex() {
  useEffect(() => {
    setMeta(
      "Conditions We Treat | Sano Acupuncture, Bronx NY",
      "Acupuncture in the Bronx for stress, pain, women's health, fertility, migraines, sleep, digestion and facial rejuvenation. Joannie Campuzano, L.Ac."
    );
  }, []);

  return (
    <main className="min-h-[80vh]" data-testid="conditions-index">
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="eyebrow mb-4">Conditions We Treat</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-6 max-w-4xl">
            Eight reasons people come back, week after week.
          </h1>
          <p className="text-lg leading-relaxed max-w-2xl mb-12" style={{ color: "var(--sano-ink-2)" }}>
            Acupuncture is not a single thing. The needles speak the body&apos;s language —
            different points, different patterns, different protocols depending on what
            you bring through the door. Here are the eight reasons clients most often do.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px" style={{ background: "var(--sano-border)" }}>
            {CONDITIONS.map((c) => (
              <Link
                key={c.slug}
                to={`/conditions/${c.slug}`}
                className="block p-8 hover:bg-[#F0EBE1] transition-colors group"
                style={{ background: "var(--sano-bg)" }}
                data-testid={`condition-card-${c.slug}`}
              >
                <div className="eyebrow mb-3" style={{ color: "var(--sano-olive)" }}>{c.eyebrow}</div>
                <h2 className="font-serif-display text-2xl leading-tight mb-3">{c.title}</h2>
                <p className="text-sm leading-relaxed mb-5" style={{ color: "var(--sano-ink-2)" }}>
                  {c.intro.split(".")[0]}.
                </p>
                <span className="inline-flex items-center gap-1.5 text-sm" style={{ color: "var(--sano-olive)" }}>
                  Learn more <ArrowRight size={14} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-16 flex flex-wrap gap-4 items-center">
            <BookingButton testid="conditions-index-book" />
            <span className="text-sm" style={{ color: "var(--sano-ink-2)" }}>
              Don&apos;t see your condition? Reach out — most things respond to skilled acupuncture.
            </span>
          </div>
        </div>
      </section>
    </main>
  );
}

export default function ConditionPage() {
  const { slug } = useParams();
  const c = CONDITIONS_BY_SLUG[slug];

  useEffect(() => {
    if (c) setMeta(c.metaTitle, c.metaDescription);
  }, [c]);

  if (!c) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center" data-testid="condition-not-found">
        <div className="text-center">
          <div className="eyebrow mb-4">Not found</div>
          <h1 className="font-serif-display text-4xl mb-4">That condition page doesn&apos;t exist.</h1>
          <Link to="/conditions" className="text-sm border-b border-current pb-0.5" style={{ color: "var(--sano-olive)" }}>
            See all conditions →
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-[80vh]" data-testid={`condition-page-${c.slug}`}>
      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="eyebrow mb-4">{c.eyebrow} · Conditions We Treat</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-6">
            {c.title}
          </h1>
          <p className="font-serif-display text-2xl sm:text-3xl italic leading-snug max-w-3xl" style={{ color: "var(--sano-olive)" }}>
            &ldquo;{c.hero}&rdquo;
          </p>
          <p className="mt-8 text-lg leading-relaxed max-w-3xl" style={{ color: "var(--sano-ink-2)" }}>
            {c.intro}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <BookingButton testid={`condition-${c.slug}-book-top`} />
            <a
              href="tel:9292726066"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium border hover:bg-[#EAEAE0]"
              style={{ borderColor: "var(--sano-ink)" }}
              data-testid={`condition-${c.slug}-call`}
            >
              Free phone consultation · 929.272.6066
            </a>
          </div>
        </div>
      </section>

      {/* Hero photo */}
      {c.image && (
        <div className="max-w-6xl mx-auto px-6 lg:px-12 mb-4">
          <img
            src={c.image}
            alt={c.title}
            className="w-full h-[55vh] object-cover rounded-sm"
            data-testid={`condition-${c.slug}-image`}
          />
        </div>
      )}

      {/* Symptoms + How it helps */}
      <section
        className="py-20 lg:py-24 border-y"
        style={{ background: "var(--sano-bg-2)", borderColor: "var(--sano-border)" }}
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16">
          <div data-testid={`condition-${c.slug}-symptoms`}>
            <div className="eyebrow mb-4">Common patterns</div>
            <h2 className="font-serif-display text-3xl mb-6">If this sounds familiar…</h2>
            <ul className="space-y-3">
              {c.symptoms.map((s, i) => (
                <li key={i} className="flex gap-3 text-base leading-relaxed" style={{ color: "var(--sano-ink-2)" }}>
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: "var(--sano-olive)" }} />
                  {s}
                </li>
              ))}
            </ul>
          </div>

          <div data-testid={`condition-${c.slug}-how`}>
            <div className="eyebrow mb-4">How acupuncture helps</div>
            <h2 className="font-serif-display text-3xl mb-6">Quiet medicine, clear results.</h2>
            <ul className="space-y-4">
              {c.howItHelps.map((h, i) => (
                <li key={i} className="flex gap-3 text-base leading-relaxed" style={{ color: "var(--sano-ink-2)" }}>
                  <Check size={18} strokeWidth={1.5} className="shrink-0 mt-0.5" style={{ color: "var(--sano-olive)" }} />
                  {h}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Treatment plan */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <div className="eyebrow mb-3">What treatment looks like</div>
          <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight mb-6">
            Your plan is yours, not a template.
          </h2>
          <p className="text-lg leading-relaxed" style={{ color: "var(--sano-ink-2)" }}>
            {c.plan}
          </p>
        </div>
      </section>

      {/* FAQ — same questions, in context */}
      <div
        className="border-y"
        style={{ background: "var(--sano-bg-2)", borderColor: "var(--sano-border)" }}
      >
        <FAQ
          eyebrow="Common questions"
          title={`Before you book — ${c.title.toLowerCase()}.`}
        />
      </div>

      {/* Final CTA */}
      <section className="py-20" style={{ background: "var(--sano-olive)", color: "var(--sano-bg)" }}>
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight mb-4">
            Ready to feel better in your body?
          </h2>
          <p className="text-base leading-relaxed mb-8 opacity-90">
            Joannie has over ten years of experience treating {c.title.toLowerCase()} — and the time we spend together is shaped around what you, specifically, bring through the door.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <BookingButton testid={`condition-${c.slug}-book-bottom`} variant="light" />
            <Link
              to="/conditions"
              className="inline-flex items-center text-sm border-b border-current pb-0.5"
              style={{ color: "var(--sano-bg)" }}
              data-testid={`condition-${c.slug}-all-conditions`}
            >
              ← All conditions we treat
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
